import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { FC, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Department, getCircleColor, getConnectorColor } from "./utils";
import { LifecycleName } from "../../../auth/roles";
import { darkSurfaces } from "../../../theme";

const darkProgress = {
  notStarted: "#2b3740",
  connector: "rgba(205, 218, 228, 0.18)",
  ring: "rgba(205, 218, 228, 0.28)"
};

type Props = {
  departments: Department[];
  /** Whether the signed-in user may open a given lifecycle. Kept as a prop so each book supplies its own rule. */
  canAccess: (lifecycle: LifecycleName) => boolean;
  minWidth?: number;
};

/**
 * The circle-and-connector strip on an entity detail page. Takes its departments as a list rather than as
 * one prop per lifecycle, so a book with five and a book with seven both use the same component.
 */
export const DepartmentProgress: FC<Props> = ({
  departments,
  canAccess,
  minWidth = 700
}) => {
  const navigate = useNavigate();
  const isDark = useTheme().palette.mode === "dark";

  // The shared colours are tuned for a white page. On the dark page the light grey "not started" circle and
  // white ring glare, so dark mode sinks not-started into the surface and rings circles in the
  // page colour instead.
  const circleColor = (status: Department["status"]) =>
    isDark && status === "not-started"
      ? darkProgress.notStarted
      : getCircleColor(status);
  const connectorColor = (index: number) => {
    const color = getConnectorColor(index, departments);
    return isDark && departments[index].status !== "completed"
      ? darkProgress.connector
      : color;
  };

  return (
    <Box
      sx={{ padding: 2, overflowX: "auto", paddingTop: 3, paddingBottom: 3 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth,
          position: "relative"
        }}
      >
        {departments.map((department, index) => {
          const accessible = canAccess(department.key);

          return (
            <Fragment key={department.key}>
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flex: 1
                  }}
                >
                  <Tooltip
                    title={
                      accessible
                        ? `${department.label} ${department.status}`
                        : `${department.label} - access restricted`
                    }
                    placement="top"
                    arrow
                  >
                    <Box sx={{ position: "relative", marginBottom: 1 }}>
                      <Box
                        aria-disabled={!accessible}
                        onClick={() => {
                          if (accessible) {
                            navigate(department.key);
                          }
                        }}
                        sx={{
                          width: 50,
                          height: 50,
                          cursor: accessible ? "pointer" : "not-allowed",
                          borderRadius: "50%",
                          backgroundColor: circleColor(department.status),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: isDark
                            ? `3px solid ${darkSurfaces.canvas}`
                            : "3px solid #fff",
                          boxShadow: isDark
                            ? `0 0 0 1px ${darkProgress.ring}`
                            : "0 2px 4px rgba(0,0,0,0.2)",
                          opacity: accessible ? 1 : 0.42
                        }}
                      />
                    </Box>
                  </Tooltip>

                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: "center",
                      fontSize: "11px",
                      color: accessible
                        ? isDark
                          ? "text.secondary"
                          : "#666"
                        : "text.disabled",
                      maxWidth: 80,
                      lineHeight: 1.2
                    }}
                  >
                    {department.label}
                  </Typography>
                </Box>
              </Box>
              {index < departments.length - 1 ? (
                <Box
                  sx={{
                    height: 3,
                    backgroundColor: connectorColor(index),
                    flex: 2.4,
                    marginLeft: "-18px",
                    marginRight: "-18px",
                    marginTop: -2.5
                  }}
                />
              ) : null}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
